import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaStar, FaGift, FaRocket, FaTrophy, FaFire } from 'react-icons/fa';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

const FlowContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  color: white;
  margin: 20px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: float 6s ease-in-out infinite;
    pointer-events: none; /* allow clicks through decorative overlay */
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;

const FlowHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
`;

const FlowTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const FlowSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
`;

const TasksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
`;

const TaskCard = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  position: relative;
  
  ${props => props.completed && `
    background: rgba(72, 187, 120, 0.3);
    border-color: rgba(72, 187, 120, 0.5);
  `}
  
  ${props => props.current && `
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: scale(1.05);
  `}
`;

const TaskNumber = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => props.completed ? '#48bb78' : props.current ? '#f6ad55' : 'rgba(255, 255, 255, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 15px;
  color: white;
`;

const TaskTitle = styled.h3`
  font-size: 1.2rem;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TaskDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0 0 15px 0;
  line-height: 1.4;
`;

const TaskReward = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressSection = styled.div`
  text-align: center;
  margin-top: 30px;
  position: relative;
  z-index: 1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  overflow: hidden;
  margin: 20px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #48bb78, #38a169);
  width: ${props => props.progress}%;
  transition: width 0.5s ease;
  border-radius: 4px;
`;

const ProgressText = styled.p`
  font-size: 1.1rem;
  margin: 10px 0;
  font-weight: 600;
`;

const AchievementBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 15px;
  margin: 20px 0;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const BadgeIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
  color: #f6ad55;
`;

const BadgeText = styled.p`
  font-size: 1rem;
  margin: 0;
  font-weight: 600;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin: 10px auto;
  position: relative;
  z-index: 2; /* ensure button is above decorative overlay */
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const FirstTasksFlow = ({ user, tasks, onCompleteTask, onStartNextTask, onComplete }) => {
  const [completedTasks, setCompletedTasks] = useState(0);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const history = useHistory();

  // Robust navigation helpers: try history.push, fall back to window.location
  const navigate = (path) => {
    try {
      if (history && typeof history.push === 'function') {
        history.push(path);
        console.log('[Onboarding] Navigated via history.push to', path);
      } else {
        console.warn('[Onboarding] history.push not available, falling back to window.location:', path);
        window.location.assign(path);
      }
    } catch (err) {
      console.error('[Onboarding] navigation error, falling back to window.location:', err);
      window.location.assign(path);
    }
  };

  const handlePrimaryAction = () => {
    console.log('[Onboarding] Primary action clicked; completedTasks=', completedTasks, 'currentTaskIndex=', currentTaskIndex);
    if (completedTasks === 0) {
      if (typeof onComplete === 'function') {
        try { onComplete(); } catch (err) { console.warn('[Onboarding] onComplete threw:', err); }
      }
      navigate('/user'); // navigate to user page (was '/form')
    } else {
      if (typeof onStartNextTask === 'function') {
        onStartNextTask(currentTaskIndex);
      } else {
        console.warn('[Onboarding] onStartNextTask not provided');
      }
    }
  };

  const handleExploreFeatures = () => {
    console.log('[Onboarding] Explore features clicked');
    if (typeof onComplete === 'function') {
      try { onComplete(); } catch (err) { console.warn('[Onboarding] onComplete threw:', err); }
    }
    navigate('/features');
  };

  const firstTasks = [
    {
      id: 1,
      title: 'Create Your First Task',
      description: 'Add a simple task to get started. It could be anything - from "Check emails" to "Take a walk".',
      reward: 'Unlock Random Mode',
      icon: <FaRocket />
    },
    {
      id: 2,
      title: 'Complete Your First Task',
      description: 'Mark your first task as complete and experience the satisfaction of progress!',
      reward: 'Unlock Start Mode',
      icon: <FaCheckCircle />
    },
    {
      id: 3,
      title: 'Try a Productivity Mode',
      description: 'Use Random, Start, or Flow mode to tackle your next task and see the magic happen.',
      reward: 'Unlock Flow Mode',
      icon: <FaStar />
    }
  ];

  useEffect(() => {
    // Calculate completed tasks based on user progress
    const completed = tasks.filter(task => task.completed).length;
    setCompletedTasks(Math.min(completed, 3));
    
    if (completed < 3) {
      setCurrentTaskIndex(completed);
    }
  }, [tasks]);

  const progress = (completedTasks / 3) * 100;

  const getAchievementMessage = () => {
    if (completedTasks === 0) {
      return {
        icon: <FaRocket />,
        text: 'Ready to start your productivity journey?'
      };
    } else if (completedTasks === 1) {
      return {
        icon: <FaFire />,
        text: 'Great start! You\'re building momentum!'
      };
    } else if (completedTasks === 2) {
      return {
        icon: <FaStar />,
        text: 'Almost there! One more task to unlock everything!'
      };
    } else {
      return {
        icon: <FaTrophy />,
        text: 'Congratulations! You\'re now a productivity master!'
      };
    }
  };

  const achievement = getAchievementMessage();

  return (
    <FlowContainer>
      <FlowHeader>
        <FlowTitle>
          <FaGift />
          Your First 3 Tasks
        </FlowTitle>
        <FlowSubtitle>
          Complete these tasks to unlock all features and see immediate value
        </FlowSubtitle>
      </FlowHeader>

      <TasksGrid>
        {firstTasks.map((task, index) => (
          <TaskCard
            key={task.id}
            completed={index < completedTasks}
            current={index === currentTaskIndex && completedTasks < 3}
          >
            <TaskNumber completed={index < completedTasks} current={index === currentTaskIndex}>
              {index < completedTasks ? <FaCheckCircle /> : index + 1}
            </TaskNumber>
            
            <TaskTitle>
              {task.icon}
              {task.title}
            </TaskTitle>
            
            <TaskDescription>{task.description}</TaskDescription>
            
            <TaskReward>
              <FaGift />
              {task.reward}
            </TaskReward>
          </TaskCard>
        ))}
      </TasksGrid>

      <ProgressSection>
        <ProgressText>
          Progress: {completedTasks}/3 tasks completed
        </ProgressText>
        
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>

        <AchievementBadge>
          <BadgeIcon>{achievement.icon}</BadgeIcon>
          <BadgeText>{achievement.text}</BadgeText>
        </AchievementBadge>

        {completedTasks < 3 && (
          <ActionButton
            onClick={handlePrimaryAction}
          >
            {completedTasks === 0 ? 'Create Your First Task' : 
             completedTasks === 1 ? 'Complete Your Task' : 
             'Try a Productivity Mode'}
            <FaRocket />
          </ActionButton>
        )}

        {completedTasks === 3 && (
          <ActionButton onClick={handleExploreFeatures}>
            Explore All Features
            <FaTrophy />
          </ActionButton>
        )}
      </ProgressSection>
    </FlowContainer>
  );
};

export default FirstTasksFlow;
